import React, { useState, useEffect, useRef, Suspense } from "react";
import { lastClick, trackClicks } from "./utility/lastClick";
import { Redirect, Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useSpring, animated } from "react-spring";

import Home, { primeHome } from "./Screens/Home";

import { TransitionOverlay } from "./Components/TransitionOverlay";
import { NotificationBox } from "./Components/NotificationBox";
import { TranslationBoundary } from "./NewComponents/TranslationSafe";
import { HelpPanelHost } from "./NewComponents/HelpPanel";
import { CheckoutLanguageProvider } from "./i18n/checkout";

import { TransitionContext } from "./Context/TransitionContext";
import { NotificationContext } from "./Context/NotificationContext";
import { AuthContext } from "./Context/AuthContext";
import { DataContext } from "./Context/DataContext";

import { PrivateRoute } from "./Components/PrivateRoute";
import { HeadedRoute } from "./Components/HeadedRoute";

import "./App.css";

import { AccountType, EAST1, EAST4 } from "./utility/enums";
import Loader from "./NewComponents/Loader";
import StaticPage, { STATIC_PAGES } from "./Components/StaticPage";
import PortalSkeleton, { isPortalPath } from "./NewComponents/PortalSkeleton";
import TopLoadingBar from "./NewComponents/LoadingBar";
import { auth, firestore, getFullHttpUrl, PAYPAL_OPTIONS, STRIPE_ID, signedOutKnown } from "./firebase.config";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import CustomToast from "./Components/shared/customToast";
import { collectAllUserDetails } from "./utility/GETUSERDETAILS";
import { HistoryTracker, useScreenTransition } from "./Hooks/useScreenTransition";
import { FunctionWarmer } from "./utility/warmFunctions";
import { ClarityFields } from "./utility/clarityFields";
import ChangePasswordWarning from "./NewComponents/ChangePasswordWarning";
import WhatsAppHelp from "./NewComponents/WhatsAppHelp";
import FastTooltip from "./NewComponents/FastTooltip";

import { lazyPage, lazyGroup, PageFallback, ChunkErrorBoundary, primeLazy } from "./utility/lazyPage";
import { usePayPalWanted, wantPaymentScripts } from "./utility/paymentScripts";
import { captureReferralFromUrl } from "./utility/referrals";
import { captureLanguageContextFromUrl } from "./utility/languageContext";
import { captureAdFlowFromUrl } from "./utility/adFlow";
import { captureEntryPage } from "./utility/entryPage";
import { clearPageCache } from "./utility/pageCache";

// Every screen but the home is its own download (src/utility/lazyPage.js).
// Once a screen of an area has loaded, the rest of its area follows in the
// background (lazyGroup), so moving around the office or the account does not
// wait again. Rendering a screen where people pay (the checkout, payment links,
// Settings > Billing) also starts loading PayPal and Stripe.
const screen = (factory) => lazyPage(factory);
const checkoutScreen = lazyGroup({ onRender: wantPaymentScripts });
const quoteScreen = lazyGroup();
const accountScreen = lazyGroup();
const officeScreen = lazyGroup();
const translatorScreen = lazyGroup();

// sessionStorage key for the blocked-IP answer (App: isIpBlocked).
const IP_CHECK_KEY = "ipBlockCheck";

const OrderContact = checkoutScreen(() => import("./Screens/Order/OrderContact"));
const OrderDocuments = checkoutScreen(() => import("./Screens/Order/OrderDocuments"));
const OrderOptions = checkoutScreen(() => import("./Screens/Order/OrderOptions"));
const OrderPayment = checkoutScreen(() => import("./Screens/Order/OrderPayment"));
const OrderType = checkoutScreen(() => import("./Screens/Order/OrderType"));
const MaintenancePage = screen(() => import("./Screens/Order/MaintainacePage"));

const QuoteContact = quoteScreen(() => import("./Screens/Quote/QuoteContact"));
const QuoteDetails = quoteScreen(() => import("./Screens/Quote/QuoteDetails"));
const QuoteOptions = quoteScreen(() => import("./Screens/Quote/QuoteOptions"));
const QuoteCreate = quoteScreen(() => import("./Screens/Quote/backend/QuoteCreate"));
const QuoteCreateDetails = quoteScreen(() => import("./Screens/Quote/backend/QuoteCreateDetails"));
const QuoteService = quoteScreen(() => import("./Screens/Quote/backend/QuoteService"));

const Quotes = accountScreen(() => import("./Screens/Account/Quotes"));
const SingleQuote = accountScreen(() => import("./Screens/Account/SingleQuote"));
const Orders = accountScreen(() => import("./Screens/Account/Orders"));
const SingleOrder = accountScreen(() => import("./Screens/Account/SingleOrder"));
// Settings downloads in the background with the rest of the account (it was
// left out while its address form carried the 8 MB list of cities; it now
// fetches one country's cities when one is chosen, see utility/cities.js),
// so opening it from the menu doesn't wait for its file (~66 KB gz).
const Setting = accountScreen(() => import("./Screens/Account/Settings"), { onRender: wantPaymentScripts });
const TeamMembers = accountScreen(() => import("./Screens/Account/TeamMembers"));
// A team's Billing (EV-249) has its own page and menu item.
const BillingPage = accountScreen(() => import("./Screens/Account/BillingPage"), { onRender: wantPaymentScripts });
const Referrals = accountScreen(() => import("./Screens/Account/Referrals"));
const AccountInvoices = accountScreen(() => import("./Screens/Account/Invoices"));
const SingleInvoice = accountScreen(() => import("./Screens/Account/SingleInvoice"));
const PayInvoices = accountScreen(() => import("./Screens/Account/PayInvoices"));
const OnBoardingScreen = accountScreen(() => import("./Screens/CreateAccount/OnBoardingScreens"));
// An invoice or quote summary on its own page (opened in a new tab, see utility/documentLinks).
const PrintDocument = screen(() => import("./Screens/Account/PrintDocument"));
// Payment links: pays, so it starts PayPal and Stripe.
const MakePayment = accountScreen(() => import(/* webpackChunkName: "make-payment" */ "./Screens/Account/MakePayment"), { onRender: wantPaymentScripts });

const Coupons = officeScreen(() => import("./Screens/Backend/Coupons"));
const SingleTeam = officeScreen(() => import("./Screens/Backend/SingleTeam"));
const Teams = officeScreen(() => import("./Screens/Backend/Teams"));
const Users = screen(() => import("./Screens/Backend/Users"));
const Preferences = officeScreen(() => import("./Screens/Backend/Preferences"));
const SingleQuoteBackend = officeScreen(() => import("./Screens/Backend/SingleQuote"));
const SinglePayment = officeScreen(() => import("./Screens/Backend/SinglePayment"));
const SingleNotification = officeScreen(() => import("./Screens/Backend/SingleNotification"));
const Notifications = officeScreen(() => import("./Screens/Backend/Notifications"));
const SingleUser = officeScreen(() => import("./Screens/Backend/SingleUser"));
const ManualOrder = officeScreen(() => import("./Screens/Backend/ManualOrder"));
const Employees = officeScreen(() => import("./Screens/Backend/Employees"));
const SingleInvoiceBackend = officeScreen(() => import("./Screens/Backend/SingleInvoice"));
const OfficeInvoices = officeScreen(() => import("./Screens/Backend/Invoices"));
const Receivables = officeScreen(() => import("./Screens/Backend/Receivables"));
const AbandonedCheckouts = officeScreen(() => import("./Screens/Backend/AbandonedCheckouts"));
const EmailLog = officeScreen(() => import("./Screens/Backend/EmailLog"));
const OfficeInterpretations = officeScreen(() => import("./Screens/Backend/Interpretations"));
const InterpretationEditor = officeScreen(() => import("./Screens/Backend/InterpretationEditor"));
const QuotesBackend = officeScreen(() => import("./Screens/Backend/Quotes"));
const OrdersBackend = officeScreen(() => import("./Screens/Backend/Orders"));
const SingleOrderBackend = officeScreen(() => import("./Screens/Backend/SingleOrder"));
const Dashboard = officeScreen(() => import("./Screens/Backend/Dashboard"));
const OfficeReferrals = officeScreen(() => import("./Screens/Backend/Referrals"));

const SingleRevision = translatorScreen(() => import("./Screens/Backend/SingleRevision"));
const SingleTranslation = translatorScreen(() => import("./Screens/Backend/SingleTranslation"));
const Guidelines = translatorScreen(() => import("./Screens/Backend/Guidelines"));
const OwnRevisions = translatorScreen(() => import("./Screens/Backend/OwnRevisions"));
const OwnTranslations = translatorScreen(() => import("./Screens/Backend/OwnTranslations"));
const OwnInvoices = translatorScreen(() => import("./Screens/Backend/OwnInvoices"));
const OwnPayments = translatorScreen(() => import("./Screens/Backend/OwnPayments"));

const MarketingLandingPage = screen(() => import(/* webpackChunkName: "partner" */ "./Screens/MarketingLandingPage"));

const Certified = screen(() => import("./Screens/Certified"));
const Standard = screen(() => import("./Screens/Standard"));
const Interpretations = screen(() => import("./Screens/Interpretations"));
const Languages = screen(() => import("./Screens/Languages"));
const FAQ = screen(() => import("./Screens/FAQ"));
const OrderLookup = screen(() => import("./Screens/OrderLookup"));
const Verify = screen(() => import("./Screens/Verify"));
const Contact = screen(() => import("./Screens/Contact"));
const Terms = screen(() => import("./Screens/Terms"));
const Refunds = screen(() => import("./Screens/Refunds"));
const Privacy = screen(() => import("./Screens/Privacy"));
const Reviews = screen(() => import("./Screens/Reviews"));
const JoinTeam = screen(() => import("./Screens/JoinTeam"));
const NotFound = screen(() => import("./Screens/NotFound"));
const Unsubscribe = screen(() => import("./Screens/Unsubscribe"));
// The interpreter's page of a booked interpretation (no account).
const InterpreterAssignment = screen(() => import("./Screens/InterpreterAssignment"));
// Where the answers of the "How did it go?" email after an interpretation lead (no account).
const InterpretationFeedback = screen(() => import("./Screens/InterpretationFeedback"));
// Dev only (never in a production build): office's Interpretations with fake
// quotes, no sign-in, no Firestore (Screens/Backend/InterpretationsDemo).
const InterpretationsDemo = process.env.NODE_ENV === "development" ? screen(() => import("./Screens/Backend/InterpretationsDemo")) : null;
// Dev only (never in a production build): the office order page's Files tab
// with a fake order, to try dropping files (Screens/Backend/FilesDropDemo).
const FilesDropDemo = process.env.NODE_ENV === "development" ? screen(() => import("./Screens/Backend/FilesDropDemo")) : null;
const ReferralLanding = screen(() => import("./Screens/ReferralLanding"));
const FAQs = screen(() => import("./Screens/FAQs/Faq"));
const WarningHeader = screen(() => import("./NewComponents/WarningHeader"));

// The screen of the address the visitor landed on starts downloading now,
// while the sign-in state is still being read, instead of after it (each
// account and office page: they used to wait for the person's profile, a
// round trip, before asking for their file; only Orders was ahead).
const PRELOADS = [
  [/^\/office\/(revisions|revision\/|guidelines|translation\/)/, OwnTranslations],
  [/^\/office\/translations\/./, SingleOrderBackend],
  [/^\/office\/quotes/, QuotesBackend],
  [/^\/office\/quote\//, SingleQuoteBackend],
  [/^\/office\/invoices/, OfficeInvoices],
  [/^\/office\/invoice\//, SingleInvoiceBackend],
  [/^\/office\/teams/, Teams],
  [/^\/office\/team\//, SingleTeam],
  [/^\/office\/dashboard/, Dashboard],
  [/^\/office\/receivables/, Receivables],
  [/^\/office\/abandoned-checkouts/, AbandonedCheckouts],
  [/^\/office\/email-log/, EmailLog],
  [/^\/office\/interpretations/, OfficeInterpretations],
  [/^\/office\/interpretation\//, InterpretationEditor],
  [/^\/office\/employees/, Employees],
  [/^\/office\/coupons/, Coupons],
  [/^\/office\/referrals/, OfficeReferrals],
  [/^\/office\/user\//, SingleUser],
  [/^\/office\/new-(order|quote)/, ManualOrder],
  [/^\/office(\/|$)/, OrdersBackend],
  [/^\/account\/onboarding/, OnBoardingScreen],
  [/^\/account\/print\//, PrintDocument],
  [/^\/account\/team(-members)?\/?$/, TeamMembers],
  [/^\/account\/billing/, BillingPage],
  [/^\/account\/referrals/, Referrals],
  [/^\/account\/settings/, Setting],
  [/^\/account\/quotes/, Quotes],
  [/^\/account\/invoices/, AccountInvoices],
  [/^\/account\/pay-invoices/, PayInvoices],
  [/^\/account\/quote\//, SingleQuote],
  [/^\/account\/order\//, SingleOrder],
  [/^\/account\/invoice\//, SingleInvoice],
  [/^\/account(\/|$)/, Orders],
  [/^\/order(\/|$)/, OrderDocuments],
  [/^\/(quote|manualQuote)(\/|$)/, QuoteDetails],
  [/^\/make-payment\/invoices?\//, SingleInvoice],
  [/^\/make-payment\//, MakePayment],
  [/^\/partner\/?$/, MarketingLandingPage],
  [/^\/certified/, Certified],
  [/^\/standard/, Standard],
  [/^\/interpretations/, Interpretations],
  [/^\/languages/, Languages],
  [/^\/faq/, FAQs],
  [/^\/contact/, Contact],
  [/^\/reviews/, Reviews],
  [/^\/terms/, Terms],
  [/^\/refunds/, Refunds],
  [/^\/privacy/, Privacy],
  [/^\/verify/, Verify],
  [/^\/order-lookup/, OrderLookup],
  [/^\/join-team\//, JoinTeam],
  [/^\/u\//, Unsubscribe],
  [/^\/interpreter\//, InterpreterAssignment],
  [/^\/r\//, ReferralLanding],
];
const landing = PRELOADS.find(([pattern]) => pattern.test(window.location.pathname));
if (landing) {
  landing[1].preload().catch(() => {});
}

// Another firm's referral code in the address (?ref=<code>) is remembered for
// the signup (utility/referrals.js); /r/<code> does the same (ReferralLanding).
captureReferralFromUrl();
// The language of the article someone came from (?doclang=), for the
// checkout's language selector (utility/languageContext.js). Before the
// router exists, so it never sees the parameter.
captureLanguageContextFromUrl();
// The ad pages' shorter checkout (?flow=ad, utility/adFlow.js). The same way.
captureAdFlowFromUrl();
// The site page the visitor came from, for the abandoned-checkout follow-up
// (utility/entryPage.js).
captureEntryPage();

// For a prerendered page (index.js): resolves when the screen of this address
// can be drawn without passing through its loading fallback.
export const primeLanding = () => {
  if (window.location.pathname === "/") return primeHome();
  return landing ? primeLazy(landing[1]) : Promise.resolve();
};


function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// The team ID of the person signed in here, kept in this browser so the next
// load can start reading the team together with the profile. Only an ID
// (teams are readable by ID anyway); gone when they sign out (localStorage
// is cleared) and replaced when the profile says otherwise.
const TEAM_OF_KEY = "everbary.teamOf:";
const knownTeamOf = (uid) => {
  try {
    return localStorage.getItem(TEAM_OF_KEY + uid) || null;
  } catch (e) {
    return null;
  }
};
const rememberTeam = (uid, teamId) => {
  try {
    if (teamId) localStorage.setItem(TEAM_OF_KEY + uid, teamId);
    else localStorage.removeItem(TEAM_OF_KEY + uid);
  } catch (e) {
    // Storage off: the team loads after the profile, as before.
  }
};

const App = () => {
  const [windowSize, setWindowSize] = useState({
    height: window.document.documentElement.clientHeight,
    width: window.document.documentElement.clientWidth,
    orientation:
      window.document.documentElement.clientWidth >
      window.document.documentElement.clientHeight
        ? "landscape"
        : "portrait",
  });

  const [color, setColor] = useState(false);

  // PayPal's SDK loads once a payment screen is opened, not on every page.
  const payPalWanted = usePayPalWanted();

  const [showPreloader, setShowPreloader] = useState(false);
  const preloaderProps = useSpring({
    opacity: showPreloader ? 1 : 0,
    pointerEvents: showPreloader ? "all" : "none",
  });

  const [webP, setWebP] = useState(null);

  useEffect(() => {
    const checkForWebP = async () => {
      if (!window.self.createImageBitmap) {
        setWebP("png");
        return false;
      }

      const webpData =
        "data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=";
      const blob = await fetch(webpData).then((r) => r.blob());
      const supportsWebP = await createImageBitmap(blob).then(
        () => true,
        () => false
      );

      setWebP(supportsWebP ? "webp" : "png");
    };

    checkForWebP();
  }, []);

  useEffect(() => {
    sleep(1000).then(() => {
      setShowPreloader(false);
    });
  }, []);

  useEffect(() => {
    setWindowSize({
      height: window.document.documentElement.clientHeight,
      width: window.document.documentElement.clientWidth,
      orientation:
        window.document.documentElement.clientWidth >
        window.document.documentElement.clientHeight
          ? "landscape"
          : "portrait",
    });
  }, []);

  const [manualQuote, setManualQuote] = useState({
    parsed: false,
  });

  const [quote, setQuote] = useState({
    name: "",
    email: "",
    parsed: false,
  });

  const [order, setOrder] = useState({
    orderID: "",
    name: "",
    email: "",
    parsed: false,
  });

  useEffect(() => {
    //localStorage.clear();
    if (localStorage.getItem("order")) {
      let mOrder = JSON.parse(localStorage.getItem("order"));

      if (mOrder.files && typeof mOrder.files === "string") {
        try {
          mOrder.files = JSON.parse(mOrder.files);
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }

      setOrder({ ...mOrder, parsed: true });
    } else {
      localStorage.setItem("order", JSON.stringify({ ...order, parsed: true }));
      setOrder({ ...order, parsed: true });
    }

    if (localStorage.getItem("quote")) {
      let mQuote = JSON.parse(localStorage.getItem("quote"));

      if (mQuote.files) {
        mQuote.files = JSON.parse(mQuote.files);
      }

      setQuote({ ...mQuote, parsed: true });
    } else {
      localStorage.setItem("quote", JSON.stringify({ ...quote, parsed: true }));
      setQuote({ ...quote, parsed: true });
    }

    if (localStorage.getItem("manualQuote")) {
      let mQuote = JSON.parse(localStorage.getItem("manualQuote"));

      if (mQuote.files) {
        mQuote.files = JSON.parse(mQuote.files);
      }

      setManualQuote({ ...mQuote, parsed: true });
    } else {
      // Under its own key: writing this under "quote" emptied the visitor's
      // saved quote request on every load (read once, then lost on a reload).
      localStorage.setItem("manualQuote", JSON.stringify({ ...manualQuote, parsed: true }));
      setManualQuote({ ...manualQuote, parsed: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signed out and already known to be (index.js waits for Auth on prerendered
  // pages): the first render is the page itself, not the loader.
  const [signedIn, setSignedIn] = useState(() => !signedOutKnown());

  const clearSignOut = () => {
    setOrder({ orderID: "", name: "", email: "", parsed: false });
    setQuote({ name: "", email: "", parsed: false });
    setUserData(null);
    // The pages' last-visit copies (utility/pageCache) go with everything else.
    clearPageCache();
    localStorage.clear();
  };

  const [userData, setUserData] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(() => !signedOutKnown());
  const [loadingTeam, setLoadingTeam] = useState(false);
  let unsubscribeUserData = useRef({});
  let unsubscribeTeamData = useRef({});
  // The team the listener above is for: the user document changes often, the team rarely.
  const subscribedTeamId = useRef(null);

  useEffect(() => {
    let unSub;
    try{

      setLoadingAuth(true);
      unSub = auth.onAuthStateChanged(async(user) => {
      if (user && user.uid) {
        // The team this person was on last time starts loading together with
        // the profile, not a round trip after it (rememberTeam). If the
        // profile says another team (or none), the listener is replaced.
        const knownTeam = knownTeamOf(user.uid);
        if (knownTeam && subscribedTeamId.current !== knownTeam) {
          subscribedTeamId.current = knownTeam;
          subscribeTeamData(knownTeam);
        }
        subscribeUserData(user.uid);
        setSignedIn(true);
        // For public/index.html: preconnect on the next visit.
        try { localStorage.setItem("everbary.signedIn", "1"); } catch (e) {}

        const docRef = firestore.collection("users").doc(user.uid);

        await docRef
          .get()
          .then(async (doc) => {
            if (!doc.exists) {
              await docRef.set({ email: user.email }, { merge: true });
              setLoadingAuth(false);
            }
          })
          .catch((error) => {
            setLoadingAuth(false);
            auth.signOut();
            clearSignOut();
          });
        } else {
          setSignedIn(false);
          setLoadingAuth(false);
          try { localStorage.removeItem("everbary.signedIn"); } catch (e) {}
        }

        // Set loading false once userData is set
        // setTimeout(() => {
        //   setLoadingUser(false);
        // }, 1000);
    })
    }catch(e){
      console.log(e);
    }finally{
      setLoadingAuth(false);
    }

    return () => {
      unSub && unSub();
      if (typeof unsubscribeTeamData.current === "function") {
        unsubscribeTeamData.current();
      }
      if (typeof unsubscribeTeamData.current === "function") {
        unsubscribeTeamData.current();
      }
    };
  }, []);

  const subscribeUserData = (userID) => {
    try{
      setLoadingUser(true);
      unsubscribeUserData.current = firestore
      .collection("users")
      .doc(userID)
      .onSnapshot(function (doc) {
        if (doc.exists && doc.data().teamStatus === "deactivated") {
          // Their team deactivated them (team-deactivateMember): their sign-in
          // is off; a session that was open ends now, not when it runs out.
          signOutDeactivated();
          return;
        }
        if (doc.exists) {
          setUserData({ ...doc.data(), id: userID });
          rememberTeam(userID, doc.data().teamId);
          if(doc.data().teamId){
            // Only a new team needs a new listener, and the old one is closed first.
            if (subscribedTeamId.current !== doc.data().teamId) {
              if (typeof unsubscribeTeamData.current === "function") {
                unsubscribeTeamData.current();
              }
              // What the old listener had (the team remembered from last time) goes too.
              setTeamData(null);
              subscribedTeamId.current = doc.data().teamId;
              subscribeTeamData(doc.data().teamId);
            }
          }else{
            setTeamData(null);
            if (typeof unsubscribeTeamData.current === "function") {
              unsubscribeTeamData.current();
            }
            unsubscribeTeamData.current = {};
            subscribedTeamId.current = null;
          }
        }
      });
    }catch(e){
      console.log(e)
    }finally{
      setUserLoaded(true);
      setLoadingUser(false);
    }
  };
  const signOutDeactivated = () => {
    if (typeof unsubscribeUserData.current === "function") {
      unsubscribeUserData.current();
    }
    if (typeof unsubscribeTeamData.current === "function") {
      unsubscribeTeamData.current();
    }
    unsubscribeTeamData.current = {};
    subscribedTeamId.current = null;
    setTeamData(null);
    auth.signOut().catch(() => {});
    clearSignOut();
    setNotification({ show: true, title: "Signed out", type: "error",
      text: "Your team's owner or admin deactivated your account. Ask them to reactivate it, or contact us." });
    sleep(8000).then(() => setNotification((n) => ({ ...n, show: false })));
  };

  const subscribeTeamData = (teamId) => {
    try{
      setLoadingTeam(true);
      unsubscribeTeamData.current = firestore
        .collection("teams")
        .doc(teamId)
        .onSnapshot(function (doc) {
          if (doc.exists) {
            setTeamData({ ...doc.data(), id: teamId });
          }
        });
    }catch(e){
      console.log(e)
    }finally{
      setUserLoaded(true);
      setLoadingTeam(false);
    }
  };

  // useEffect(() => {
  //   if (signedIn && userData && userData.teamId) {
  //     setLoadingTeam(true);
  //     subscribeTeamData(userData.teamId);
  //   } else {
  //     if (typeof unsubscribeTeamData.current === "function") {
  //       unsubscribeTeamData.current();
  //     }
  //     unsubscribeTeamData.current = {};
  //   }

  //   return () => {
  //     if (typeof unsubscribeTeamData.current === "function") {
  //       unsubscribeTeamData.current();
  //     }
  //   };
  // }, [userData, signedIn]);


  const [showTransitionOverlay, setShowTransitionOverlay] = useState(false);

  useEffect(() => {
    const onResize = () => {
      setWindowSize({
        height: window.document.documentElement.clientHeight,
        width: window.document.documentElement.clientWidth,
        orientation:
          window.document.documentElement.clientWidth >
          window.document.documentElement.clientHeight
            ? "landscape"
            : "portrait",
      });
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const setBodyLock = (status) => {
    if (status) {
      document.getElementById("app").style.height = `${windowSize.height}px`;
      document.getElementById("app").style.overflow = `hidden`;
    } else {
      document.getElementById("app").style.height = `auto`;
      document.getElementById("app").style.overflow = `auto`;
    }
  };

  // Starts empty: the hidden box is part of the page, so whatever it holds is
  // in the prerendered HTML of every route. It used to hold a placeholder
  // success message, which is what crawlers and AI assistants read at the top
  // of a dozen pages.
  const [notification, setNotification] = useState({
    show: false,
    title: "",
    text: "",
    type: "error",
  });


  // Each notice hides itself after its time unless a newer one replaced it.
  // `options.action` ({ label, onClick }) adds a button, like Undo after a
  // delete; `options.duration` is how long it stays, in ms.
  const notificationSeq = useRef(0);
  // Who a guest is, as far as the checkout knows (the saved order or quote).
  const guestContact = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(window.location.pathname.startsWith("/quote") ? "quote" : "order") || "{}");
      return { name: saved.name || `${saved.firstName || ""} ${saved.lastName || ""}`.trim(), email: saved.email || "" };
    } catch (e) {
      return {};
    }
  };
  useEffect(() => trackClicks(), []);
  const showNotification = (title, text, type, sendErrorEmail, error = "", options = {}) => {
    const seq = ++notificationSeq.current;
    const action = options.action
      ? { label: options.action.label, onClick: () => {
          if (seq === notificationSeq.current) setNotification((n) => ({ ...n, show: false }));
          options.action.onClick();
        } }
      : null;
    setNotification({ show: true, title: title, text: text, type: type, action });
    
    const pageUrl = window ? window.location.href : ""
    // A guest's notice emails only when something really failed: an error came
    // with it, or the page asked for it. "That's not an email" stays quiet.
    if(type === "error" && (userData || error || sendErrorEmail === true) ){
      // Signed in: the function takes the name and email from the token.
      const who = userData || guestContact();
      Promise.resolve(auth.currentUser ? auth.currentUser.getIdToken() : null).then((token) => axios.post(getFullHttpUrl("sendErrorEmail", EAST4),
      {userData: {name: who.name || "", email: who.email || ""}, error, notificationText: text,
      errorMessage: `${error ? error.message || "": title}${lastClick() ? ` · Clicked: ${lastClick()}` : ""}`, pageUrl}, { headers: token ? { Authorization: `Bearer ${token}` } : {} })).catch(() => {});
    }

    sleep(options.duration || 5000).then(() => {
      if (seq === notificationSeq.current) setNotification({ show: false, title: title, text: text, type: type, action });
    });
  };

  const [userBlocked, setUserBlocked] = useState(false);

  const checkUserBlocked = async () => {
    firestore.collection("blockedUsers").doc(userData.email).get().then((doc) => {
        if (doc.exists) {
            collectAllUserDetails(doc.data(), userData.email, userData.name, false);
            setUserBlocked(true);
        }
    });
}

// Whether this connection's IP is blocked: asked once per browser session (the
// answer is kept in sessionStorage), not on every change of the user document.
const ipCheck = useRef(null);
const isIpBlocked = () => {
  if (!ipCheck.current) {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(IP_CHECK_KEY));
    } catch (e) {
      saved = null;
    }
    ipCheck.current = saved && typeof saved.blocked === "boolean"
      ? Promise.resolve(saved.blocked)
      : axios.get('https://api.ipify.org/?format=json')
        .then((response) => firestore.collection("blockedIps").doc(response.data.ip).get()
          .then((doc) => {
            try {
              sessionStorage.setItem(IP_CHECK_KEY, JSON.stringify({ ip: response.data.ip, blocked: doc.exists }));
            } catch (e) {
              // Private browsing: it asks again on the next page load.
            }
            return doc.exists;
          }))
        .catch((error) => {
          ipCheck.current = null;
          throw error;
        });
  }
  return ipCheck.current;
};

// The account's email is checked once, after the IP (as before).
const checkedEmail = useRef(null);
const userEmail = userData ? userData.email : null;

useEffect(() => {
  isIpBlocked().then((blocked) => {
    if (blocked) {
      setUserBlocked(true);
    } else if (userData && userData.email && checkedEmail.current !== userData.email) {
      checkedEmail.current = userData.email;
      checkUserBlocked();
    }
  }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [userEmail]);

const checkUserName = (data) => {
  const name = data.name ? data.name.toLowerCase() : data.firstName ? (data.firstName + " " + data.lastName).toLowerCase() : "";
  if(name){
    firestore.collection("blockedNames").where("name", "==", data.name.toLowerCase()).get().then((qSnap) => {
      if(qSnap.empty){
        return;
      }
      if(qSnap.docs.length > 0){
        collectAllUserDetails("", data.email, name, true);
        // setUserBlocked(true);
      }
    })
  }
  return;
}

useEffect(() => {
    quote && quote.parsed && checkUserName(quote);
    order && order.parsed && checkUserName(order);
}, [quote, order]);


  // return ( <MaintenancePage/>);


  if (loadingUser || loadingTeam || loadingAuth || (!userData && signedIn) || userBlocked) {
    // An account or office page: its layout in grey while sign-in and the
    // profile load, instead of a spinner on an empty screen.
    if (!userBlocked && isPortalPath(window.location.pathname)) {
      return <><TopLoadingBar /><PortalSkeleton /></>;
    }
    return (
      <div className="container mx-auto w-full h-dvh flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  

  return (
    <>

    {/*<WarningHeader/>*/}
    <TransitionContext.Provider
      value={{
        setShowTransitionOverlay: setShowTransitionOverlay,
        setBodyLock: setBodyLock,
        color: color,
        setColor: setColor,
        webP: webP,
      }}
    >
      <NotificationContext.Provider
        value={{ showNotification: showNotification }}
      >
        <AuthContext.Provider
          value={{
            signedIn: signedIn,
            setSignedIn: setSignedIn,
            clearSignOut: clearSignOut,
          }}
        >
          <DataContext.Provider
            value={{
              quote: quote,
              setQuote: setQuote,
              order: order,
              setOrder: setOrder,
              userData: userData,
              userLoaded: userLoaded,
              teamData: teamData,
              manualQuote: manualQuote,
              setManualQuote: setManualQuote,
            }}
          >
            <PayPalScriptProvider
              options={PAYPAL_OPTIONS}
              deferLoading={!payPalWanted}
            >
              <div id="app" className="appContainer">
                <animated.div
                  style={preloaderProps}
                  className="preloaderOverlay"
                />

                <ChangePasswordWarning/>
                <FastTooltip />
                <CustomToast />

                <TransitionOverlay show={showTransitionOverlay} />
                {/* The portal's loading bar along the top (NewComponents/LoadingBar),
                    outside Suspense so it stays while a screen's file downloads. */}
                <TopLoadingBar />

                <NotificationBox
                  windowSize={windowSize}
                  show={notification.show}
                  title={notification.title}
                  text={notification.text}
                  type={notification.type}
                  action={notification.action}
                />


                {/* <Elements stripe={stripePromise}> */}
                <ChunkErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <HelmetProvider>
                    <Router>
                      {/* A page the browser translated can trip React; this draws it again
                          instead of leaving it blank (see TranslationSafe). */}
                      {/* The order steps in English or Spanish (see i18n/checkout). */}
                      <CheckoutLanguageProvider>
                      <TranslationBoundary>
                      {/* "Help" of the account and avatar menus (openHelp). */}
                      <HelpPanelHost />
                      <HistoryTracker />
                      {/* Starts the functions behind the page's buttons (utility/warmFunctions). */}
                      <FunctionWarmer />
                      {/* What people type, for Clarity (utility/clarityFields). */}
                      <ClarityFields user={userData} />
                      {/* WhatsApp for guests in the checkout (NewComponents/WhatsAppHelp). */}
                      <WhatsAppHelp signedIn={signedIn} loadingAuth={loadingAuth} />
                      <Switch>
                        {/* Dev only: /office/interpretations-demo (InterpretationsDemo). */}
                        {InterpretationsDemo && (
                          <Route path="/office/interpretations-demo">
                            <InterpretationsDemo />
                          </Route>
                        )}
                        {/* Dev only: /office/files-demo (FilesDropDemo). */}
                        {FilesDropDemo && (
                          <Route path="/office/files-demo">
                            <FilesDropDemo />
                          </Route>
                        )}
                        {/* {userData &&
                          (userData.role === AccountType.OWNER ||
                            userData.role === AccountType.SUPPORT ||
                            userData.role === AccountType.TRANSLATER) && (
                            <PrivateRoute path="/office">
                              <Office windowSize={windowSize} />
                            </PrivateRoute>
                          )} */}

                          

                       {
                        userData && teamData && teamData.newTeam
                       && (userData.teamRole === AccountType.OWNER || userData.teamRole === AccountType.ADMIN) &&
                          <PrivateRoute path="/account/onboarding">
                            <OnBoardingScreen windowSize={windowSize}/>
                          </PrivateRoute>
                        }
                      

                        {/* Start of Admin Routes */}

                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/dashboard`}>
                            <Dashboard windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/payments`}>
                            <OwnPayments windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/translations/:orderID`}>
                            <SingleOrderBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/translations`}>
                            <OrdersBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/quotes`}>
                            <QuotesBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/invoices`}>
                            <OfficeInvoices windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {/* Receivables: what each team or client owes (owner). */}
                        {userData && userData.role === AccountType.OWNER &&
                          <PrivateRoute path={`/office/receivables/:accountKey?`}>
                            <Receivables windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {/* Abandoned checkouts: leads that did not convert and order drafts (owner). */}
                        {userData && userData.role === AccountType.OWNER &&
                          <PrivateRoute path={`/office/abandoned-checkouts`}>
                            <AbandonedCheckouts windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {/* Email log: what happened to each email sent through SES. */}
                        {userData && [AccountType.OWNER, AccountType.SUPPORT, AccountType.SUPPORTPLUS].includes(userData.role) &&
                          <PrivateRoute path={`/office/email-log`}>
                            <EmailLog windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {/* Interpretations: interpretation quotes, from the interpreter form or made here. */}
                        {userData && [AccountType.OWNER, AccountType.SUPPORT, AccountType.SUPPORTPLUS].includes(userData.role) &&
                          <PrivateRoute path={`/office/interpretations`}>
                            <OfficeInterpretations windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && [AccountType.OWNER, AccountType.SUPPORT, AccountType.SUPPORTPLUS].includes(userData.role) &&
                          <PrivateRoute path={`/office/interpretation/:quoteID`}>
                            <InterpretationEditor windowSize={windowSize} />
                          </PrivateRoute>
                        }

                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/invoice/:itemID`}> 
                            <SingleInvoiceBackend windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORTPLUS) &&
                          <PrivateRoute path={`/office/employees`}>
                            <Employees windowSize={windowSize} />
                          </PrivateRoute>
                        }
                
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/invoices`}>
                            <OwnInvoices windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/translations`}>
                            <OwnTranslations windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/revisions`}>
                            <OwnRevisions windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/guidelines`}>
                            <Guidelines windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/new-order`}>
                            <ManualOrder windowSize={windowSize} mode={"order"} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/new-quote`}>
                            <ManualOrder windowSize={windowSize} mode={"quote"} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.OWNER &&
                          <PrivateRoute path={`/office/user/:userID`}>
                            <SingleUser windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {/* The translator's announcements and preferences. Office had its
                            own old versions of these, without a menu: removed. */}
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/notifications`}>
                            <Notifications windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/notification/:notificationID`}>
                            <SingleNotification windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        <PrivateRoute path={`/office/payment/:paymentID`}>
                          <SinglePayment windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/quote/:quoteID`}>
                          <SingleQuoteBackend windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={`/office/translation/:id/:tabParam`}>
                          <SingleTranslation windowSize={windowSize} />
                        </PrivateRoute>
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/preferences`}>
                            <Preferences windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && userData.role === AccountType.TRANSLATER &&
                          <PrivateRoute path={`/office/revision/:id`}>
                            <SingleRevision windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                          <PrivateRoute path={`/office/teams`}>
                            <Teams windowSize={windowSize} />
                          </PrivateRoute>
                        }
                        {userData && (userData.role === AccountType.OWNER ) &&
                          <PrivateRoute path={`/office/team/:id`}>
                            <SingleTeam windowSize={windowSize} />
                          </PrivateRoute>
                        }
                          {userData && (userData.role === AccountType.OWNER || userData.role === AccountType.SUPPORT) &&
                            <PrivateRoute path={`/office/coupons`}>
                              <Coupons windowSize={windowSize} />
                            </PrivateRoute>
                          }
                          {userData && (userData.role === AccountType.OWNER || userData.role === "admin") &&
                            <PrivateRoute path={`/office/referrals`}>
                              <OfficeReferrals windowSize={windowSize} />
                            </PrivateRoute>
                          }
                          {/* No Blocked Users page any more (feedback 7-oct). The blocks still
                              apply: checkUserBlocked / isIpBlocked above read blockedUsers and
                              blockedIps; entries are managed in the Firebase console now. */}
                        {/* End of Admin Routes */}



                        <HeadedRoute path="/order/payment">
                          <OrderPayment windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/options">
                          <OrderOptions windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/documents">
                          <OrderDocuments windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/contact">
                          <OrderContact windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order/type">
                          <OrderType windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/manualQuote/create">
                          <QuoteCreate windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/manualQuote/details">
                          <QuoteCreateDetails windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/manualQuote/service">
                          <QuoteService windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/quote/details">
                          <QuoteDetails windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/quote/options">
                          <QuoteOptions windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/quote/contact">
                          <QuoteContact windowSize={windowSize} />
                        </HeadedRoute>

                        {/* Old account pages: their tab in Settings (EV-client-1002). */}
                        <Route path="/account/shipping"><Redirect to="/account/settings?tab=addresses" /></Route>
                        <Route path="/account/password"><Redirect to="/account/settings?tab=profile" /></Route>
                        <Route path="/account/profile"><Redirect to="/account/settings?tab=profile" /></Route>
                        <Route exact path="/account/setting"><Redirect to="/account/settings" /></Route>
                        <PrivateRoute path="/account/invoices">
                          <AccountInvoices windowSize={windowSize} />
                        </PrivateRoute>
                        {/* Several invoices in one payment (client portal redesign). */}
                        <PrivateRoute path="/account/pay-invoices">
                          <PayInvoices windowSize={windowSize} />
                        </PrivateRoute>

                        <HeadedRoute path="/account/quote/:quoteID">
                          <SingleQuote windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/account/order/:orderID">
                          <SingleOrder windowSize={windowSize} />
                        </HeadedRoute>

                        <PrivateRoute path="/account/orders">
                          <Orders windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/quotes">
                          <Quotes windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path={["/account/team", "/account/team-members"]}>
                          <TeamMembers windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/billing">
                          <BillingPage windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/referrals">
                          <Referrals windowSize={windowSize} />
                        </PrivateRoute>
                        <PrivateRoute path="/account/settings">
                          <Setting windowSize={windowSize} />
                        </PrivateRoute>

                        {/* An invoice's payment link (the invoice emails, "Send payment link",
                            an order's Pay once it has an invoice) opens the invoice's own page:
                            how to pay, and the receipt once paid. "invoices" is the link of the
                            "Second Payment Failed" email. */}
                        <Route path={["/make-payment/invoice/:itemID", "/make-payment/invoices/:itemID"]}
                          render={({ match, location }) => (
                            <Redirect to={{ pathname: `/account/invoice/${encodeURIComponent(match.params.itemID)}`, search: location.search }} />
                          )} />
                        <HeadedRoute path="/make-payment/:item/:itemID">
                          <MakePayment windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/account/invoice/:itemID">
                          <SingleInvoice windowSize={windowSize} />
                        </HeadedRoute>
                        {/* Plain Route: the page sets its own title and noindex. */}
                        <Route path="/account/print/:item/:itemID">
                          <PrintDocument />
                        </Route>

                        <HeadedRoute path="/join-team/:inviteID">
                          <JoinTeam windowSize={windowSize} />
                        </HeadedRoute>

                        {/* <HeadedRoute path="/guides">
                          <Guides windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/differences">
                          <DifferencesGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/count">
                          <CountGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/notarization">
                          <NotarizationGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/uscis">
                          <USCISGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/foreign">
                          <ForeignGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/apostille">
                          <ApostilleGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/process">
                          <ProcessGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/glossary">
                          <GlossaryGuide windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/guide/turnaround">
                          <TurnaroundGuide windowSize={windowSize} />
                        </HeadedRoute> */}

                        <HeadedRoute path="/terms">
                          <Terms windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/refunds">
                          <Refunds windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/privacy">
                          <Privacy windowSize={windowSize} />
                        </HeadedRoute>

                        {/* 
                            <HeadedRoute path="/jobs">
                          <Jobs windowSize={windowSize} />
                        </HeadedRoute>
                        */}

                        <HeadedRoute path="/contact">
                          <Contact windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/order-lookup">
                          <OrderLookup windowSize={windowSize} />
                        </HeadedRoute>
                        {/* Opt-out link of the institutional outreach emails. Plain Route: HeadedRoute marks pages index,follow. */}
                        <Route path="/u/:code">
                          <Unsubscribe />
                        </Route>
                        {/* An interpreter's assignment, from the link office sends them (no account).
                            Plain Route: the page sets its own title and noindex. */}
                        <Route path="/interpreter/:token">
                          <InterpreterAssignment />
                        </Route>
                        {/* A client's answer to the follow-up email after an interpretation (no account).
                            Plain Route: the page sets its own title and noindex. */}
                        <Route path="/interpretation-feedback/:token">
                          <InterpretationFeedback />
                        </Route>
                        {/* Another firm's referral link. Plain Route: not a page to index. */}
                        <Route path="/r/:code">
                          <ReferralLanding />
                        </Route>
                        <HeadedRoute path="/verify">
                          <Verify windowSize={windowSize} />
                        </HeadedRoute>

                        {/* <HeadedRoute path="/sign-in">
                          <SignIn windowSize={windowSize} />
                        </HeadedRoute> */}

                        {/* <HeadedRoute path="/forgot">
                          <ForgotPassword windowSize={windowSize} />
                        </HeadedRoute> */}

                        {/* <HeadedRoute path="/create-account">
                          <CreateAccount windowSize={windowSize} />
                        </HeadedRoute> */}

                        <HeadedRoute path="/faq">
                          {/* <FAQ windowSize={windowSize} /> */}
                          <FAQs windowSize={windowSize}  />
                        </HeadedRoute>
                        <HeadedRoute path="/reviews">
                          <Reviews windowSize={windowSize} />
                        </HeadedRoute>
                        {/* /about and the /use-cases pages are static pages in public/
                            (tools/seo-pages-99.data.js, tools/seo-pages-usecases.data.js):
                            a click from the app loads them (see StaticPage). */}
                        <Route exact path={STATIC_PAGES}>
                          <StaticPage fallback={<NotFound loadingUser={loadingUser} loadingAuth={loadingAuth} loadingTeam={loadingTeam}/>} />
                        </Route>
                        <HeadedRoute path="/languages">
                          <Languages windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/standard">
                          <Standard windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/interpretations">
                          <Interpretations windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute path="/certified">
                          <Certified windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute exact path="/">
                          <Home windowSize={windowSize} />
                        </HeadedRoute>
                        <HeadedRoute exact path="/partner">
                          <MarketingLandingPage windowSize={windowSize} />
                        </HeadedRoute>

                        {/* <HeadedRoute path="/language">
                          <LanguagesRouter windowSize={windowSize} />
                        </HeadedRoute> */}
                        {/* <HeadedRoute path="/documents">
                          <Documents windowSize={windowSize} />
                        </HeadedRoute> */}
                        {/* <HeadedRoute path="/document">
                          <DocumentsRouter windowSize={windowSize} />
                        </HeadedRoute> */}
                        <HeadedRoute path="*">
                          <NotFound loadingUser={loadingUser} loadingAuth={loadingAuth} loadingTeam={loadingTeam}/>
                        </HeadedRoute>
                      </Switch>
                      </TranslationBoundary>
                      </CheckoutLanguageProvider>
                    </Router>
                  </HelmetProvider>
                </Suspense>
                </ChunkErrorBoundary>
                {/* </Elements> */}
              </div>
            </PayPalScriptProvider>
          </DataContext.Provider>
        </AuthContext.Provider>
      </NotificationContext.Provider>
    </TransitionContext.Provider>
    </>
  );
};

export default App;
